import {
  M8LegalRequesites,
  M8ProdStackDTO,
  M8UserFullInfo,
} from 'services/production/types';
import {
  TUserProfileInfo,
  TClientDetailsClientInfo,
  ProdStackInfo,
  TClientsTableItem,
  TAccountInfo,
} from 'types/state';
import { normalizeM8CompanySize } from 'services/production/utils/normalizeM8CompanySize';
import { normalizeProfileLike } from 'utils/profileLikeUtils';
import { createProdStackInfo } from 'utils/prodStackInfoUtils';
import { normalizeM8DateString } from 'services/production/utils/normalizeM8DateString';
import { Client } from 'types/clientTypes';
import { Org } from 'types/orgInfoTypes';
import { Person } from 'types/personTypes';

export const normalizeM8UserFullInfo = (user: M8UserFullInfo) => ({
  toPerson(): Person {
    return {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      middleName: user.middleName || '',
      phone: user.phone || '',
    };
  },
  toOrg(): Org {
    return {
      name: user.organization || '',
      location: user.country || '',
      staffCount: user.companySize
        ? normalizeM8CompanySize(user.companySize).toOrgStaffCount()
        : 'small',
    };
  },
  toClient(m8LegalRequisites: null | M8LegalRequesites): Client {
    return {
      id: user.id,
      org: normalizeM8UserFullInfo(user).toOrg(),
      person: normalizeM8UserFullInfo(user).toPerson(),
      position: user.position || '',
      prodStackInfo: normalizeM8UserFullInfo(user).toProdStackInfo(null),
      demoStackInfo: null,
      unp: m8LegalRequisites?.unp || null,
    };
  },
  toUserProfileInfo(): TUserProfileInfo {
    return {
      company: user.organization || '',
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      middleName: user.middleName || '',
      phone: user.phone || '',
      position: user.position || '',
      orgStaffCount: user.companySize
        ? normalizeM8CompanySize(user.companySize).toOrgStaffCount()
        : null,
      location: user.country,
      resident: user.resident,
    };
  },
  toClientDetailsClientInfo(): TClientDetailsClientInfo {
    return {
      id: user.id,
      fullName: normalizeProfileLike({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        middleName: user.middleName || '',
      }).toFullName(),
      company: user.organization || '',
      staff: user.companySize
        ? normalizeM8CompanySize(user.companySize).toOrgStaffCount()
        : null,
      position: user.position || '',
      email: user.email || '',
      phone: user.phone || '',
      location: user.country || '',
      domain: user.prodUrl ? new URL(user.prodUrl).host : null,
      updatedOn: user.updateOn ? user.updateOn : null,
      registeredOn: null,
      resident: user.resident,
      partnerCode: null,
      archived: user.userStatus === 'ARCHIVE',
    };
  },
  toProdStackInfo(m8ProdStackDTO: null | M8ProdStackDTO): ProdStackInfo {
    let prodStackInfo = createProdStackInfo();

    prodStackInfo.originURL = user.prodUrl;
    prodStackInfo.subdomain = user.prodUrl ? new URL(user.prodUrl).host : null;
    prodStackInfo.licenseDue = m8ProdStackDTO?.licenseEndDate
      ? normalizeM8DateString(m8ProdStackDTO?.licenseEndDate)
          .toDate()
          .toISOString()
      : null;

    prodStackInfo.trialDue = m8ProdStackDTO?.trialEndDate
      ? normalizeM8DateString(m8ProdStackDTO?.trialEndDate)
          .toDate()
          .toISOString()
      : null;

    return prodStackInfo;
  },
  toClientsTableItem: (): TClientsTableItem => ({
    id: user.id,
    fullName: normalizeProfileLike({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      middleName: user.middleName || '',
    }).toFullName(),
    position: user.position || '-',
    company: user.organization || '-',
    orgStaffCount: user.companySize
      ? normalizeM8CompanySize(user.companySize).toOrgStaffCount()
      : null,
    phone: user.phone || '-',
    demoStack: user.demoDnsName,
    prodStack: user.prodUrl ? new URL(user.prodUrl).host : null,
    balance: user.balance,
    registeredOn: user.createOn,
    updatedOn: user.updateOn,
    email: user.email || '-',
  }),
  toAccountInfo(): TAccountInfo {
    if (user.email) {
      return {
        email: user.email,
        id: user.id,
      };
    }

    throw new TypeError('Expected for user to have an email');
  },
});
